<script>
	const uaParser = new (require('ua-parser-js'))();
	const { getFirstFocusableElement, getLastFocusableElement } = require('utilities');

	export default {
		data() {
			const device = uaParser.getDevice();
			const isIpadOS = uaParser.getOS().name === 'Mac OS' && navigator.maxTouchPoints > 0;
			const isMobile = isIpadOS || device.type === 'mobile' || device.type === 'tablet';

			return {
				shown: false,
				dragging: false,
				baseX: 0,
				baseY: 0,
				startDragX: 0,
				startDragY: 0,
				currentDragX: 0,
				currentDragY: 0,
				width: 400,
				height: 300,
				isMobile,
				touch: false,
				refocusTarget: null,
				label: ''
			};
		},
		computed: {
			x() {
				if (this.isMobile) {
					return '2vw';
				}

				let x = this.baseX + (this.currentDragX - this.startDragX);
				if (x + this.width > window.innerWidth) {
					x -= x + this.width - window.innerWidth;
				}

				if (x < 0) {
					x = 0;
				}

				return x;
			},
			y() {
				if (this.isMobile) {
					return '2vh';
				}

				let y = this.baseY + (this.currentDragY - this.startDragY);

				if (y + this.height > window.innerHeight) {
					y -= y + this.height - window.innerHeight;
				}

				if (y < 0) {
					y = 0;
				}

				return y;
			}
		},
		created() {
			this.resizeWatcher = () => {
				this.updateDimensions();
			};
			window.addEventListener('resize', this.resizeWatcher);
		},
		beforeDestroy() {
			if (this.resizeWatcher) {
				window.removeEventListener('resize', this.resizeWatcher);
				this.resizeWatcher = null;
			}
		},
		methods: {
			open(aroundElement, label = '') {
				if (aroundElement) {
					this.label = label;
					const rect = aroundElement.getBoundingClientRect();
					this.baseY = rect.top - 100;
					this.baseX = rect.right + 5;
					this.refocusTarget = aroundElement;
				}
				this.$nextTick(() => {
					this.focusFirstItem();
					this.updateDimensions();
				});
				this.shown = true;
			},
			close() {
				if (this.refocusTarget) {
					this.refocusTarget.focus();
					this.refocusTarget = null;
				}
				this.shown = false;
				this.label = '';
			},
			updateDimensions() {
				if (this.$refs.popup) {
					this.width = this.$refs.popup.offsetWidth || 400;
					this.height = this.$refs.popup.offsetHeight || 300;
				}
			},
			setupUpListeners() {
				if (this.listeners) {
					return;
				}

				this.listeners = {};
				this.listeners.move = ev => {
					this.move(ev);
				};
				this.listeners.end = ev => {
					this.end(ev);
				};
				window.addEventListener('mousemove', this.listeners.move);
				window.addEventListener('touchmove', this.listeners.move);
				window.addEventListener('mouseup', this.listeners.end);
				window.addEventListener('touchend', this.listeners.end);
			},
			clearListeners() {
				if (!this.listeners) {
					return;
				}

				window.removeEventListener('mousemove', this.listeners.move);
				window.removeEventListener('touchmove', this.listeners.move);
				window.removeEventListener('mouseup', this.listeners.end);
				window.removeEventListener('touchend', this.listeners.end);

				this.listeners = null;
			},
			eventMatchesMode(ev) {
				return this.dragging && ((ev.type.indexOf('touch') !== -1 && this.touch) || (ev.type.indexOf('touch') === -1 && !this.touch));
			},
			start(ev) {
				if (this.dragging || this.isMobile) {
					return;
				}
				this.dragging = true;
				this.touch = ev.type.indexOf('touch') !== -1;
				this.currentDragX = this.touch ? ev.touches[0].pageX : ev.pageX;
				this.currentDragY = this.touch ? ev.touches[0].pageY : ev.pageY;
				this.startDragX = this.currentDragX;
				this.startDragY = this.currentDragY;
				this.setupUpListeners();
			},
			move(ev) {
				if (!this.eventMatchesMode(ev)) {
					return;
				}
				ev.preventDefault();
				this.currentDragX = this.touch ? ev.touches[0].pageX : ev.pageX;
				this.currentDragY = this.touch ? ev.touches[0].pageY : ev.pageY;
			},
			end(ev) {
				if (!this.eventMatchesMode(ev)) {
					return;
				}
				this.dragging = false;
				this.baseX = this.x;
				this.baseY = this.y;
				this.currentDragX = 0;
				this.currentDragY = 0;
				this.startDragX = 0;
				this.startDragY = 0;
				this.clearListeners();
			},
			focusFirstItem() {
				const popup = this.$refs.popupContent;
				const target = getFirstFocusableElement(popup) || popup;

				this.$nextTick(() => {
					target.focus();
				});
			},
			focusLastItem() {
				const popup = this.$refs.popupContent;
				const target = getLastFocusableElement(popup) || popup;

				this.$nextTick(() => {
					target.focus();
				});
			}
		}
	};
</script>
<template>
	<div
		v-show="shown"
		ref="popup"
		:style="{
			top: typeof y === 'number' ? '' + y + 'px' : y,
			left: typeof x === 'number' ? '' + x + 'px' : x
		}"
		@mousedown="start"
		@touchstart="start"
		@keydown.esc="close"
		role="dialog"
		:aria-label="label"
		:class="{ 'mobile-full-screen': isMobile }"
		class="draggable-popup">
		<button
			class="focus-capture hidden-visually"
			aria-hidden="true"
			@focus="focusLastItem"></button>

		<div
			ref="popupContent"
			tabindex="-1">
			<slot></slot>
		</div>

		<button
			class="focus-capture hidden-visually"
			aria-hidden="true"
			@focus="focusFirstItem"></button>
	</div>
</template>
<style lang="scss">
	.draggable-popup {
		position: fixed;
		top: 0;
		left: 0;
		z-index: 10000;
		background-color: white;
		width: 514px;
		max-width: 100vw;
		height: 402px;
		max-height: 100vh;

		.drag-shade {
			position: absolute;
			z-index: 100;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			opacity: 0.15;
			background-color: black;
		}

		&.mobile-full-screen {
			width: 96vw;
			height: 96vh;
		}
	}
</style>
